import { Dialog, Overlay, Text } from "@mantine/core";
import { useElementSize, useLocalStorage, useViewportSize } from "@mantine/hooks";
import { useRef, useState } from "react";
import Draggable from "react-draggable";

interface DraggableDialogProps {
    title: string;
    opened: boolean;
    onClose: () => void;
    children: React.ReactNode;
    openingPosition?: { x: number; y: number };
}

export const DraggableDialog = ({ opened, onClose, title, children, openingPosition }: DraggableDialogProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const [positionOffset, setPositionOffset] = useLocalStorage<{ x: number; y: number }>({
        key: `control-panel-container-position-${title}`,
        // render the dialog in the middle left part of the screen, if no default position is provided
        defaultValue: openingPosition ?? { x: 0.125, y: 0.5 },
    });
    const elementSize = useElementSize();
    const viewportSize = useViewportSize();
    const width = viewportSize.width - elementSize.width;
    const height = viewportSize.height - elementSize.height;

    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Dialog
            opened={opened}
            withCloseButton
            keepMounted
            onClose={onClose}
            // Anchor the dialog at the viewport origin and let react-draggable (below) be the sole
            // positioner. Mantine v9 wraps Dialog in an Affix that consumes `position`; applying the
            // saved offset here too would double it against the Draggable transform and push the
            // dialog off-screen.
            position={{ top: 0, left: 0 }}
            ref={elementSize.ref}
            renderRoot={(props) => (
                <Draggable
                    nodeRef={nodeRef}
                    bounds="body"
                    handle=".handle"
                    position={{ x: positionOffset.x * width, y: positionOffset.y * height }}
                    onStart={() => setIsDragging(true)}
                    onDrag={(_e, { x, y }) => setPositionOffset({ x: x / width, y: y / height })}
                    onStop={() => setIsDragging(false)}
                >
                    <div {...props} ref={nodeRef} />
                </Draggable>
            )}
            zIndex="var(--mantine-priority-highest)"
            portalProps={{ style: { pointerEvents: "none" } }}
            style={{ pointerEvents: "auto" }}
        >
            <Text className="handle" size="sm" mb="xs" fw={500} style={{ cursor: "move" }}>
                {title}
            </Text>
            {opened && children}
            {/* render a transparent overlay while dragging to avoid mouse movements being captured by the iframe and interfering with dragging */}
            {isDragging ? <Overlay color="#000" backgroundOpacity={0} /> : null}
        </Dialog>
    );
};

export default DraggableDialog;
